import { Flex, Typography, Tooltip } from "antd";
import { useNavigate } from "react-router-dom";
import { useStores } from "stores";
import { formatWithDecimals } from "utils";
import { useGlobalBalance } from "utils/hooks";
import { routes } from "constants/routes";
import styles from "./header.module.css";
import { getCurrencyIcon } from "./helpers";
import { useCallback, useEffect } from "react";
import { observer } from "mobx-react-lite";
import { DisplayAmount } from "ui/DisplayAmount";
import { FILTERS_KEY_ENUM, TOKEN_BALANCE_ENUM } from "interfaces";
import { DIRECT_PAYMENT_CUSTOMER_LP } from "utils/envConstants";

const BalanceItem = ({
  balance,
  currency,
  rate,
  onClick,
}: {
  balance: string | number;
  currency: string;
  rate: number;
  onClick: () => void;
}) => {
  return (
    <Tooltip
      key={currency}
      title={`Баланс ${currency} на всех транзитных кошельках в текущий момент`}
      placement="bottomLeft"
    >
      <Flex className={styles.item} gap="8px" onClick={onClick}>
        {getCurrencyIcon(currency)}
        <Flex vertical justify="space-between">
          <DisplayAmount amount={Number(balance)} currency={currency} />
          {rate && (
            <Typography.Text className={styles.secondaryText}>Курс: {formatWithDecimals(rate, 2)}</Typography.Text>
          )}
        </Flex>
      </Flex>
    </Tooltip>
  );
};

export const BalancesBlock = observer(() => {
  const { user, settings, bids } = useStores();
  const navigate = useNavigate();
  const balance = useGlobalBalance(user, settings);

  const { isCustomer, isProvider, isOperator } = user.roles;

  const filtersKey = isOperator ? FILTERS_KEY_ENUM.POSITIVE_BALANCES : FILTERS_KEY_ENUM.ONLY_MY_FUNDS;

  const onBalanceClick = useCallback(
    (currency?: string) => {
      bids.clear();

      bids.onChangeDataFlow({
        filters: { balanceCurrency: [currency], [filtersKey]: true },
        sorters: bids.sorters,
      });

      navigate(`/${routes.bids}`);
    },
    [navigate, bids]
  );

  if (!balance) {
    return null;
  }

  return (
    <Flex className={`${styles.header} ${styles.balancesBlock}`}>
      <Flex className={styles.item} onClick={() => onBalanceClick()}>
        <Typography.Text>Общий баланс:</Typography.Text>
      </Flex>

      {!DIRECT_PAYMENT_CUSTOMER_LP && (
        <BalanceItem
          balance={balance.rubBalance}
          currency={TOKEN_BALANCE_ENUM.RUB}
          rate={settings.rates.rates[TOKEN_BALANCE_ENUM.RUB]}
          onClick={() => onBalanceClick(TOKEN_BALANCE_ENUM.RUB)}
        />
      )}

      {balance.tokens &&
        Object.entries(balance.tokens).map(([token, amount]) => (
          <BalanceItem
            key={token}
            balance={amount}
            currency={token}
            rate={settings.rates.rates[token]}
            onClick={() => onBalanceClick(token)}
          />
        ))}
      {balance.natives &&
        Object.entries(balance.natives).map(([network, amount]) => (
          <BalanceItem
            key={network}
            balance={amount}
            currency={network}
            rate={settings.rates.rates[network]}
            onClick={() => onBalanceClick(network)}
          />
        ))}
    </Flex>
  );
});
